import { useMemo, useState } from 'react'
import type { KeyboardEvent, PointerEvent } from 'react'
import { formatClock, type SeriesDef, type TrendSample } from './live-utils'

const SURFACE = '#0d192a'
const GRID_COLOR = 'rgba(148, 178, 210, 0.14)'
const CROSSHAIR_COLOR = 'rgba(148, 178, 210, 0.35)'
const TEXT_MUTED = '#8194ad'

const WIDTH = 300
const HEIGHT = 104
const MARGIN = { top: 8, right: 10, bottom: 18, left: 30 }

// 横轴用整分钟级别的整洁刻度（30秒起步）
const TIME_STEPS = [30, 60, 120, 180, 300, 600, 900, 1200, 1800, 2700, 3600]

function pickTimeStep(span: number): number {
  return TIME_STEPS.find((step) => step >= span / 4.5) ?? 7200
}

function formatTick(seconds: number): string {
  if (seconds === 0) return '0'
  return seconds % 60 === 0 ? `${seconds / 60}分` : `${seconds}秒`
}

type Props = {
  points: TrendSample[]
  series: SeriesDef[]
  // 与 series 顺序对应的当前值（图例常驻展示）
  current: number[] | null
  digits: number
  suffix: string
}

export function StatChart({ points, series, current, digits, suffix }: Props) {
  const [active, setActive] = useState<number | null>(null)

  const domain = useMemo(() => {
    const maxV = Math.max(
      1,
      ...points.flatMap((item) => series.map((def) => item[def.key])),
      ...(current ?? [0]),
    )
    const last = points.length ? points[points.length - 1].t : 0
    return { xMin: points.length ? points[0].t : 0, xMax: Math.max(last, 60), yMax: niceCeil(maxV * 1.05) }
  }, [points, series, current])

  const plotW = WIDTH - MARGIN.left - MARGIN.right
  const plotH = HEIGHT - MARGIN.top - MARGIN.bottom
  const x = (t: number) => MARGIN.left + ((t - domain.xMin) / (domain.xMax - domain.xMin)) * plotW
  const y = (v: number) => MARGIN.top + plotH - (v / domain.yMax) * plotH

  const paths = useMemo(() => {
    const xScale = (t: number) => MARGIN.left + ((t - domain.xMin) / (domain.xMax - domain.xMin)) * plotW
    const yScale = (v: number) => MARGIN.top + plotH - (v / domain.yMax) * plotH
    return series.map((def) => ({
      color: def.color,
      d: points.map((item, index) => `${index ? 'L' : 'M'}${xScale(item.t).toFixed(1)},${yScale(item[def.key]).toFixed(1)}`).join(''),
    }))
  }, [points, series, domain, plotW, plotH])

  const xTicks = useMemo(() => {
    const step = pickTimeStep(domain.xMax - domain.xMin)
    const ticks: number[] = []
    for (let value = Math.ceil(domain.xMin / step) * step; value <= domain.xMax + 1e-6; value += step) ticks.push(value)
    return ticks
  }, [domain.xMin, domain.xMax])

  const yTicks = useMemo(() => [0, domain.yMax / 2, domain.yMax], [domain.yMax])

  const activePoint = active != null ? points[active] : undefined
  const lastPoint = points.length ? points[points.length - 1] : undefined

  const nearestIndex = (event: PointerEvent<SVGSVGElement>) => {
    const rect = event.currentTarget.getBoundingClientRect()
    const px = ((event.clientX - rect.left) / rect.width) * WIDTH
    let best = -1
    let bestDist = Infinity
    points.forEach((item, index) => {
      const dist = Math.abs(x(item.t) - px)
      if (dist < bestDist) {
        bestDist = dist
        best = index
      }
    })
    return best
  }

  const handleKeyDown = (event: KeyboardEvent<SVGSVGElement>) => {
    if (!points.length) return
    const step = { ArrowLeft: -1, ArrowRight: 1 }[event.key]
    if (step) {
      event.preventDefault()
      setActive((current) => Math.min(Math.max((current ?? points.length - 1) + step, 0), points.length - 1))
    } else if (event.key === 'Home') {
      event.preventDefault()
      setActive(0)
    } else if (event.key === 'End') {
      event.preventDefault()
      setActive(points.length - 1)
    } else if (event.key === 'Escape') {
      setActive(null)
    }
  }

  return (
    <div className="trend-card">
      <div className="trend-legend">
        {series.map((def, index) => (
          <span key={def.key} className="legend-item">
            <i style={{ background: def.color }} />
            {def.label} <b>{current ? `${current[index].toFixed(digits)}${suffix}` : '—'}</b>
          </span>
        ))}
      </div>
      {points.length < 2 ? (
        <p className="trend-empty">仿真推进后显示曲线</p>
      ) : (
        <svg
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          role="img"
          aria-label={`${series.map((def) => def.label).join('、')}随仿真时间变化的曲线`}
          tabIndex={0}
          onPointerMove={(event) => setActive(nearestIndex(event))}
          onPointerLeave={() => setActive(null)}
          onFocus={() => setActive(points.length - 1)}
          onBlur={() => setActive(null)}
          onKeyDown={handleKeyDown}
        >
          {yTicks.map((value) => (
            <g key={value}>
              <line x1={MARGIN.left} x2={WIDTH - MARGIN.right} y1={y(value)} y2={y(value)} stroke={GRID_COLOR} strokeWidth={1} />
              <text x={MARGIN.left - 5} y={y(value) + 3} textAnchor="end" fontSize={9} fill={TEXT_MUTED}>{value}</text>
            </g>
          ))}
          {xTicks.map((value) => (
            <text key={value} x={x(value)} y={HEIGHT - 6} textAnchor="middle" fontSize={9} fill={TEXT_MUTED}>{formatTick(value)}</text>
          ))}
          {activePoint && (
            <line x1={x(activePoint.t)} x2={x(activePoint.t)} y1={MARGIN.top} y2={MARGIN.top + plotH} stroke={CROSSHAIR_COLOR} strokeWidth={1} />
          )}
          {paths.map((item) => (
            <path key={item.color} d={item.d} fill="none" stroke={item.color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
          ))}
          {lastPoint && series.map((def) => (
            <circle key={def.key} cx={x(lastPoint.t)} cy={y(lastPoint[def.key])} r={4} fill={def.color} stroke={SURFACE} strokeWidth={2} />
          ))}
          {activePoint && series.map((def) => (
            <circle key={def.key} cx={x(activePoint.t)} cy={y(activePoint[def.key])} r={3.5} fill={def.color} stroke={SURFACE} strokeWidth={2} />
          ))}
        </svg>
      )}
      {activePoint && (
        <div className="trend-tooltip" style={{ left: `${Math.min(Math.max((x(activePoint.t) / WIDTH) * 100, 20), 80)}%` }}>
          <p className="tt-time">{formatClock(activePoint.t)}</p>
          {series.map((def) => (
            <p key={def.key}>
              <i style={{ background: def.color }} />
              <b>{activePoint[def.key].toFixed(digits)}{suffix}</b>
              {def.label}
            </p>
          ))}
        </div>
      )}
    </div>
  )
}

// 取不小于 rough 的 1/2/5 整数步长，保证纵轴刻度是整洁数值
function niceCeil(rough: number): number {
  const target = Math.max(rough, 1e-6)
  let scale = 1
  for (;;) {
    for (const multiple of [1, 2, 5, 10]) {
      const step = multiple * scale
      if (step >= target) return step
    }
    scale *= 10
  }
}
